import React, {useEffect, useRef, useState} from 'react';
import Landmark from '@core/scripts/components/landmark-adapter';
import {Icon} from "@bonprix/design-system";

let startLandmarkSent = false;
let stopLandmarkSent = false;
let progress50LandmarkSent = false;
let progress100LandmarkSent = false;

export default function ProductVideo({video, itemNumber}) {
    const videoRef = useRef(null);
    const duration = useRef(null);
    const visibleRef = useRef(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const [showPlayButton, setShowPlayButton] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [iconVariant, setIconVariant] = useState("pause");

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold: 0.5 }
        );

        if (videoRef.current) observer.observe(videoRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        visibleRef.current = isVisible;
        if (videoRef.current) {
            if (isVisible) videoRef.current.play();
            else {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        }
    }, [isVisible]);

    useEffect(() => {
        return () => {
            if (visibleRef.current && !stopLandmarkSent) {
                sendLandmark('stop', video.videoUrl, itemNumber, duration.current);
                stopLandmarkSent = true;
            }
            duration.current = null;
        };
    }, []);

    useEffect(() => {
        if (videoRef.current?.duration) duration.current = videoRef.current.duration;
    }, [videoRef.current]);

    const onClick = () => {
        setShowPlayButton(true);
        setFadeOut(false);
        setTimeout(() => setFadeOut(true), 500);
        setTimeout(() => setShowPlayButton(false), 1000);

        if (videoRef.current.paused) {
            videoRef.current.play();
            setIconVariant("pause");
        } else {
            videoRef.current.pause();
            setIconVariant("play");
        }
    };

    const onPlay = () => {
        if (!startLandmarkSent) {
            sendLandmark('start', video.videoUrl, itemNumber, videoRef.current.duration);
            startLandmarkSent = true;
        }
    };

    const onPause = () => {
        if (!stopLandmarkSent) {
            sendLandmark('stop', video.videoUrl, itemNumber, videoRef.current.duration);
            stopLandmarkSent = true;
        }
    };

    const onTimeUpdate = () => {
        const currentTime = videoRef.current.currentTime;
        const duration = videoRef.current.duration;

        if (!progress50LandmarkSent && currentTime >= duration / 2) {
            sendLandmark('progress_50', video.videoUrl, itemNumber, duration);
            progress50LandmarkSent = true;
        }

        if (!progress100LandmarkSent && currentTime >= duration - 0.2) {
            sendLandmark('complete', video.videoUrl, itemNumber, duration);
            progress100LandmarkSent = true;
        }
    };

    const sendLandmark = (action, videoUrl, itemNumber, duration) => {
        const parts = videoUrl.split('/');
        const fileName =  parts.slice(parts.length - 2).join('_');
        const videoId = fileName.substring(0, fileName.lastIndexOf('.'));

        Landmark.sendLandmark({
            landmark: 'video',
            action: action,
            videoId: videoId,
            itemNumber: itemNumber,
            filename: fileName,
            videoLength: Math.round(duration)
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
        }
    };

    return (
        <div className={`product-image__wrapper -video relative focus:outline-none`} style={isFocused ? {borderWidth: '1.5px', borderStyle: 'solid',} : {}}
            tabIndex={0} onClick={onClick} onKeyDown={handleKeyDown} onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)} aria-label={video.altText}
        >
            <img
                className="w-full absolute left-0"
                src={video.fallbackImage}
                srcSet={`${video.fallbackImage} 1x, ${video.fallbackImage2x} 2x`}
                alt={video.altText}
            />
            <video className="w-full absolute left-0" ref={videoRef} key={video.videoUrl} muted loop playsInline
                onPlay={onPlay} onPause={onPause} onTimeUpdate={onTimeUpdate} title={video.altText}
            >
                <source src={video.videoUrl} type="video/mp4" />
            </video>

            {showPlayButton && (
                <div
                    className={`flex items-center justify-center w-12 h-12 absolute rounded transition-opacity duration-500 
        ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
                    style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                    aria-hidden="true"
                >
                    <Icon className="w-6 h-6 fill-clr-white" variant={iconVariant} />
                </div>
            )}
        </div>
    );
}
